import PageSEO from '@/components/PageSEO';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Phone, Home, Building, Fence } from 'lucide-react';
import ProudlyServingSection from '@/components/ProudlyServingSection';

export const GlassRailingsPage = () => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Glass Railing Installation",
    "name": "Custom Glass Railings",
    "description": "Frameless glass railings for decks, balconies, staircases, and pool fences with stainless steel hardware and code-compliant installation.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Comal Glass Company",
      "telephone": "+1-830-359-9925"
    },
    "areaServed": { "@type": "State", "name": "Texas" }
  };

  // FAQ data for glass railings
  const railingFaqs = [
    {
      question: "What thickness of glass is used for glass railings?",
      answer: "We use 1/2\" (12mm) fully tempered safety glass for all railing applications. This thickness is required by building codes for guardrail applications and provides the strength needed to withstand the required 200 lb concentrated load test."
    },
    {
      question: "What is the difference between 304 and 316 stainless steel hardware?",
      answer: "304 stainless steel is the standard grade suitable for interior applications and protected exterior areas. 316 stainless steel (marine-grade) contains molybdenum for enhanced corrosion resistance and is required for pool areas, coastal environments, and high-humidity locations."
    },
    {
      question: "Are glass railings safe for pool fencing?",
      answer: "Yes, glass pool fences are extremely safe when properly installed. They meet all IBC/IRC pool barrier codes, have no climbable horizontal rails, and provide unobstructed sightlines for supervising swimmers. We use 316 marine-grade stainless hardware for all pool installations."
    },
    {
      question: "What mounting systems are available for glass railings?",
      answer: "We offer four main mounting systems: base shoe channels (aluminum channel at floor level), post-mount with clamps (round posts with glass clamps), standoff pins (decorative cylindrical fasteners), and floor-mount spigots (glass mounted in anchored cores). Each has aesthetic and practical advantages."
    },
    {
      question: "Do glass railings meet building codes?",
      answer: "Yes, all our glass railing installations meet IBC and IRC building codes. This includes 42\" minimum guardrail height, the 4\" sphere test (no openings larger than 4\"), and 200 lb point load and line load requirements. We pull permits when required."
    },
    {
      question: "Can glass railings be installed on existing decks?",
      answer: "Yes, we regularly retrofit glass railings onto existing decks, balconies, and staircases. We assess the structural capacity of your existing framing and recommend the appropriate mounting system. Some installations may require minor structural reinforcement."
    }
  ];

  // FAQ Schema for AI engines
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": railingFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const features = [
    'Frameless glass panels',
    'Stainless steel posts & hardware',
    'Indoor and outdoor applications',
    'Code-compliant installations',
    'Tempered safety glass',
    'Custom heights and lengths',
    'Various glass tints available',
    'Professional installation included',
  ];

  const types = [
    {
      icon: Home,
      title: 'Deck & Patio Railings',
      description: 'Transform your outdoor living space with unobstructed views and modern elegance.',
    },
    {
      icon: Building,
      title: 'Staircase Railings',
      description: 'Interior glass railings that maximize light and create a stunning focal point.',
    },
    {
      icon: Fence,
      title: 'Pool Fences & Balcony Railings',
      description: 'Sleek glass pool fences and balcony barriers that provide safety without sacrificing your view.',
    },
  ];

  const styles = [
    {
      name: 'Frameless Glass',
      description: 'Pure glass panels with minimal hardware for the cleanest look.',
    },
    {
      name: 'Post-Mounted',
      description: 'Glass panels secured with elegant stainless steel posts.',
    },
    {
      name: 'Base Shoe',
      description: 'Glass secured at the base with an aluminum channel system.',
    },
    {
      name: 'Standoff System',
      description: 'Glass attached with decorative stainless steel standoffs.',
    },
  ];

  return (
    <div className="min-h-screen">
      <PageSEO title="Glass Railings" description="Custom glass railings for decks, balconies, staircases, and pool fences. Frameless panels, stainless steel hardware, indoor and outdoor. Serving the Texas Hill Country." path="/glass-railings" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/portfolio/image-2.jpg"
            alt="Modern glass railing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        </div>

        <div className="container-custom relative z-10 text-white">
          <div className="max-w-3xl">
            <Badge className="mb-6 bg-primary/20 text-primary border-primary/30 backdrop-blur-sm">
              Modern Railing Solutions
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-gradient-cyan">Glass Railings</span> & Pool Fences
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed">
              Elevate your space with stunning glass railings and glass pool fences that combine safety, durability, and unobstructed views. Perfect for decks, balconies, staircases, pools, and more.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" asChild>
                <a href="tel:8303599925">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Types Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Applications</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Perfect for Any <span className="text-gradient-cyan">Space</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From residential decks to commercial staircases, our glass railings enhance any environment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {types.map((type, index) => {
              const Icon = type.icon;
              return (
                <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{type.title}</h3>
                    <p className="text-muted-foreground">{type.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recent Projects Gallery */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <Badge className="mb-4">Recent Work</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Hill Country <span className="text-gradient-cyan">Glass Railings</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Recent spigot-mounted glass railing installation featuring matte black hardware and frameless panels with stunning Hill Country views.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative group overflow-hidden rounded-xl shadow-lg">
              <img
                src="/images/portfolio/glass-railing-hillcountry-1.jpeg"
                alt="Spigot-mounted glass railing with matte black hardware overlooking Hill Country"
                className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-white font-semibold">Deck Railing with Spigot Mounts</p>
                  <p className="text-gray-300 text-sm">Matte black hardware • Frameless panels • Hill Country views</p>
                </div>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-xl shadow-lg">
              <img
                src="/images/portfolio/glass-railing-hillcountry-2.jpeg"
                alt="Extended glass railing system along patio with panoramic Hill Country views"
                className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-white font-semibold">Patio Glass Railing System</p>
                  <p className="text-gray-300 text-sm">Extended run • Floor-mounted spigots • Unobstructed views</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <Button variant="outline" asChild>
              <Link to="/portfolio">View Full Portfolio</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Why Choose Glass Railings</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Features & <span className="text-gradient-cyan">Benefits</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-none shadow-md">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                    <p className="text-sm">{feature}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Styles Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">Design Options</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Choose Your <span className="text-gradient-cyan">Style</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {styles.map((style, index) => (
              <Card key={index} className="border-none bg-card">
                <CardContent className="pt-6 text-center">
                  <h3 className="text-xl font-semibold mb-2">{style.name}</h3>
                  <p className="text-muted-foreground text-sm">{style.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4">Why Glass?</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                The Clear Choice for <span className="text-gradient-cyan">Modern Living</span>
              </h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Unobstructed Views</h3>
                    <p className="text-muted-foreground text-sm">
                      Enjoy panoramic views without visual barriers. Perfect for scenic properties.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Low Maintenance</h3>
                    <p className="text-muted-foreground text-sm">
                      Glass doesn't rust, rot, or require painting. Simple cleaning keeps it looking new.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Increased Property Value</h3>
                    <p className="text-muted-foreground text-sm">
                      Glass railings add a premium, modern aesthetic that boosts curb appeal and home value.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Weather Resistant</h3>
                    <p className="text-muted-foreground text-sm">
                      Tempered glass withstands Texas weather while providing wind protection.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/portfolio/Image-4.jpeg"
                alt="Modern glass railing balcony"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Our Process</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Simple & <span className="text-gradient-cyan">Seamless</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', description: 'Free on-site consultation to discuss your vision and take measurements.' },
              { step: '02', title: 'Design', description: 'Custom design with your choice of glass type, hardware, and mounting style.' },
              { step: '03', title: 'Fabrication', description: 'Precision cutting and tempering of your custom glass panels.' },
              { step: '04', title: 'Installation', description: 'Professional installation by our experienced team.' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-gradient-cyan mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proudly Serving Section */}
      <ProudlyServingSection serviceSlug="glass-railings" />

      {/* FAQ Section with Schema */}
      <section className="py-20 bg-secondary/50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <Badge className="mb-4">Common Questions</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Glass Railing <span className="text-gradient-cyan">FAQs</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {railingFaqs.map((faq, index) => (
              <Card key={index} className="border-none shadow-sm">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Ready for <span className="text-gradient-cyan">Beautiful Views?</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Contact us today for a free consultation and quote on your glass railing project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="tel:8303599925">
                  <Phone className="mr-2 h-5 w-5" />
                  (830) 359-9925
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GlassRailingsPage;
